import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clock,
  Download,
  Briefcase,
  Building2,
  Calendar,
  PlusCircle,
  Play,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Settings2,
  ShieldCheck,
  Trash2,
  Edit,
  Activity,
  Loader2,
} from "lucide-react";

export default function MarcajesPage() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "administrador";
  const isHRorAdmin = user?.rol === "administrador" || user?.rol === "rrhh";

  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);

  // Generador de reporte
  const [selectedEmpresaId, setSelectedEmpresaId] = useState("");
  const formatLocalDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const todayStr = formatLocalDate(new Date());
  const lastWeekStr = formatLocalDate(new Date(Date.now() - 7 * 86400000));
  const [startDate, setStartDate] = useState(lastWeekStr);
  const [endDate, setEndDate] = useState(todayStr);

  // Departamentos (CrossChex) y su mapeo a VacaGest
  const [departamentos, setDepartamentos] = useState([]);
  const [selectedDeptos, setSelectedDeptos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [selectedPuestos, setSelectedPuestos] = useState([]);
  const [syncingDeptos, setSyncingDeptos] = useState(false);
  const [syncProgress, setSyncProgress] = useState("");

  const [generating, setGenerating] = useState(false);
  const [generationJob, setGenerationJob] = useState(null);

  // Modal Empresa
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    nombre: "",
    proveedor: "CrossChex Cloud",
    api_url: "https://api.us.crosschexcloud.com/",
    api_key: "",
    api_secret: "",
    timezone_offset: -5,
    activo: true,
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);

  const loadEmpresas = async () => {
    setLoadingEmpresas(true);
    try {
      const res = await api.getEmpresasMarcaje();
      setEmpresas(res.data || []);
      if (res.data?.length > 0 && !selectedEmpresaId) {
        setSelectedEmpresaId(res.data[0].id);
      }
    } catch (err) {
      toast.error("Error al cargar empresas de marcaje");
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadCatalogos = async (empresaId) => {
    if (!empresaId) {
      setDepartamentos([]);
      setPuestos([]);
      return;
    }
    try {
      const [resDeptos, resPuestos] = await Promise.all([
        api.getDepartamentosMarcaje(empresaId),
        api.getPuestosMarcaje(empresaId),
      ]);
      setDepartamentos(resDeptos.data || []);
      setPuestos(resPuestos.data || []);
    } catch (err) {
      setDepartamentos([]);
      setPuestos([]);
    }
  };

  const handleSyncDepartamentos = async () => {
    if (!selectedEmpresaId) {
      toast.error("Selecciona una empresa");
      return;
    }
    setSyncingDeptos(true);
    setSyncProgress("Iniciando detección...");
    try {
      const initRes = await api.sincronizarDepartamentosMarcaje(selectedEmpresaId, {
        empresa_id: selectedEmpresaId,
        start_date: startDate,
        end_date: endDate,
      });
      const jobId = initRes.data.job_id;

      // La detección recorre todo el rango por lotes (31s entre páginas en CrossChex),
      // así que se sigue por el mismo endpoint de estado que los reportes.
      const interval = setInterval(async () => {
        try {
          const pollRes = await api.getEstadoTrabajoMarcaje(jobId);
          const { status, progress, error, departamentos: deps, puestos: pus, total_records } = pollRes.data;
          setSyncProgress(progress || "");

          if (status === "completed") {
            clearInterval(interval);
            setSyncingDeptos(false);
            setSyncProgress("");
            await loadCatalogos(selectedEmpresaId);
            toast.success(
              `Se detectaron ${deps?.length || 0} departamentos y ${pus?.length || 0} puestos en ${total_records} marcajes revisados`
            );
          } else if (status === "error") {
            clearInterval(interval);
            setSyncingDeptos(false);
            setSyncProgress("");
            toast.error(error || "Error al detectar departamentos y puestos");
          }
        } catch (pollErr) {
          clearInterval(interval);
          setSyncingDeptos(false);
          setSyncProgress("");
          toast.error("Error al consultar el progreso de la detección");
        }
      }, 2500);
    } catch (err) {
      setSyncingDeptos(false);
      setSyncProgress("");
      toast.error(err.response?.data?.detail || "Error al detectar departamentos desde CrossChex");
    }
  };

  const toggleDepartamento = (nombre) => {
    setSelectedDeptos((prev) =>
      prev.includes(nombre) ? prev.filter((d) => d !== nombre) : [...prev, nombre]
    );
  };

  const togglePuesto = (nombre) => {
    setSelectedPuestos((prev) =>
      prev.includes(nombre) ? prev.filter((p) => p !== nombre) : [...prev, nombre]
    );
  };

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    setSelectedDeptos([]);
    setSelectedPuestos([]);
    loadCatalogos(selectedEmpresaId);
  }, [selectedEmpresaId]);

  const handleGenerateExcel = async () => {
    if (!selectedEmpresaId) {
      toast.error("Selecciona una empresa");
      return;
    }
    setGenerating(true);
    setGenerationJob({
      status: "processing",
      progress: "Iniciando extracción por lotes de CrossChex Cloud...",
    });

    try {
      const initRes = await api.iniciarGeneracionMarcaje({
        empresa_id: selectedEmpresaId,
        start_date: startDate,
        end_date: endDate,
        departamentos: selectedDeptos,
        puestos: selectedPuestos,
      });
      const jobId = initRes.data.job_id;

      const interval = setInterval(async () => {
        try {
          const pollRes = await api.getEstadoTrabajoMarcaje(jobId);
          const { status, progress, error, filename, total_records } = pollRes.data;
          setGenerationJob({ jobId, status, progress, error, filename, total_records });

          if (status === "completed") {
            clearInterval(interval);
            setGenerating(false);

            // Descargar el archivo Excel generado
            const dlRes = await api.descargarTrabajoMarcaje(jobId);
            const url = window.URL.createObjectURL(new Blob([dlRes.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename || `Marcajes_${startDate}_al_${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success(`¡Reporte descargado con éxito! (${total_records} marcajes procesados)`);
            setTimeout(() => setGenerationJob(null), 5000);
          } else if (status === "error") {
            clearInterval(interval);
            setGenerating(false);
            toast.error(error || "Error al generar el reporte");
          }
        } catch (pollErr) {
          clearInterval(interval);
          setGenerating(false);
          toast.error("Error al consultar el progreso del reporte");
        }
      }, 2500);
    } catch (err) {
      setGenerating(false);
      setGenerationJob(null);
      toast.error(err.response?.data?.detail || "Error al iniciar la descarga de marcajes");
    }
  };

  const handleTestConnection = async (companyId) => {
    setTestingConnection(true);
    try {
      const res = await api.testEmpresaMarcaje(companyId);
      toast.success(res.data.message || "Conexión exitosa con la API de CrossChex Cloud");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo en la conexión con la API de CrossChex");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleOpenNewCompany = () => {
    setEditingCompanyId(null);
    setCompanyForm({
      nombre: "",
      proveedor: "CrossChex Cloud",
      api_url: "https://api.us.crosschexcloud.com/",
      api_key: "",
      api_secret: "",
      timezone_offset: -5,
      activo: true,
    });
    setIsCompanyModalOpen(true);
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.nombre || !companyForm.api_key || !companyForm.api_secret) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }
    setSavingCompany(true);
    try {
      if (editingCompanyId) {
        await api.updateEmpresaMarcaje(editingCompanyId, companyForm);
        toast.success("Empresa actualizada exitosamente");
      } else {
        await api.createEmpresaMarcaje(companyForm);
        toast.success("Empresa configurada exitosamente");
      }
      setIsCompanyModalOpen(false);
      loadEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar empresa");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("¿Seguro que deseas eliminar la configuración de esta empresa?")) return;
    try {
      await api.deleteEmpresaMarcaje(companyId);
      toast.success("Empresa eliminada");
      loadEmpresas();
    } catch (err) {
      toast.error("Error al eliminar empresa");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-sky-100 rounded-lg text-sky-600">
              <Clock className="w-6 h-6" />
            </div>
            Control de Marcajes y Asistencia
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Integración de APIs biométricas y generación de reportes consolidados (CrossChex Cloud)
          </p>
        </div>
      </div>

      <Tabs defaultValue="reportes" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="reportes" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Generador de Reportes de Asistencia
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="configuracion" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Configuración de Empresas & APIs ({empresas.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Generador de Reportes */}
        <TabsContent value="reportes" className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Parámetros de Descarga
              </CardTitle>
              <CardDescription>
                Selecciona la empresa y el período para consultar las marcas registradas y generar el archivo Excel
                resumido.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emp_sel">Empresa Configurada *</Label>
                  <Select value={selectedEmpresaId} onValueChange={setSelectedEmpresaId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Seleccionar empresa..." />
                    </SelectTrigger>
                    <SelectContent>
                      {empresas.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.nombre} ({e.proveedor})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="start_d">Fecha de Inicio *</Label>
                  <Input
                    id="start_d"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_d">Fecha de Fin *</Label>
                  <Input
                    id="end_d"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Filtro por Departamento */}
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Building2 className="w-4 h-4 text-sky-600" />
                    Departamentos a descargar
                    <span className="font-normal text-xs text-slate-500">
                      {selectedDeptos.length === 0
                        ? "(todos)"
                        : `(${selectedDeptos.length} seleccionado${selectedDeptos.length > 1 ? "s" : ""})`}
                    </span>
                  </Label>
                  <div className="flex items-center gap-2">
                    {departamentos.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedDeptos(departamentos.map((d) => d.dept_crosschex))}
                        disabled={selectedDeptos.length === departamentos.length}
                        className="h-7 text-xs text-slate-600"
                      >
                        Seleccionar todos
                      </Button>
                    )}
                    {selectedDeptos.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedDeptos([])}
                        className="h-7 text-xs text-slate-600"
                      >
                        Limpiar selección
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSyncDepartamentos}
                      disabled={syncingDeptos || !selectedEmpresaId}
                      className="h-7 text-xs border-slate-300"
                    >
                      {syncingDeptos ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          Consultando...
                        </>
                      ) : (
                        <>
                          <Activity className="w-3.5 h-3.5 mr-1.5 text-sky-600" />
                          Detectar departamentos
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {syncProgress && (
                  <p className="flex items-center gap-2 text-xs text-sky-700">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {syncProgress}
                  </p>
                )}

                {departamentos.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    Aún no hay departamentos registrados para esta empresa. Usa "Detectar departamentos" o genera un
                    reporte: los departamentos de CrossChex se registran automáticamente.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {departamentos.map((dep) => {
                      const activo = selectedDeptos.includes(dep.dept_crosschex);
                      return (
                        <button
                          key={dep.id}
                          type="button"
                          onClick={() => toggleDepartamento(dep.dept_crosschex)}
                          className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                            activo
                              ? "border-sky-500 bg-sky-600 text-white"
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          {dep.dept_crosschex}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-slate-500">
                  Sin selección se descargan todos los departamentos. El Excel trae una hoja por departamento.
                  "Detectar departamentos" revisa todo el rango de fechas elegido, por lotes.
                </p>
              </div>

              {/* Filtro por Puesto (Position de CrossChex) */}
              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="flex items-center gap-2 text-slate-700">
                    <Briefcase className="w-4 h-4 text-sky-600" />
                    Puestos a descargar
                    <span className="font-normal text-xs text-slate-500">
                      {selectedPuestos.length === 0
                        ? "(todos)"
                        : `(${selectedPuestos.length} seleccionado${selectedPuestos.length > 1 ? "s" : ""})`}
                    </span>
                  </Label>
                  <div className="flex items-center gap-2">
                    {puestos.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedPuestos(puestos.map((pu) => pu.puesto))}
                        disabled={selectedPuestos.length === puestos.length}
                        className="h-7 text-xs text-slate-600"
                      >
                        Seleccionar todos
                      </Button>
                    )}
                    {selectedPuestos.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedPuestos([])}
                        className="h-7 text-xs text-slate-600"
                      >
                        Limpiar selección
                      </Button>
                    )}
                  </div>
                </div>

                {puestos.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    Aún no hay puestos registrados para esta empresa. Usa "Detectar departamentos" o genera un reporte:
                    el puesto (Position) de cada colaborador se registra automáticamente.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {puestos.map((pu) => {
                      const activo = selectedPuestos.includes(pu.puesto);
                      return (
                        <button
                          key={pu.id}
                          type="button"
                          onClick={() => togglePuesto(pu.puesto)}
                          className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                            activo
                              ? "border-violet-500 bg-violet-600 text-white"
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          {pu.puesto}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-slate-500">
                  El puesto se compara sin distinguir mayúsculas ni acentos, porque en CrossChex se escribe a mano.
                </p>
              </div>

              {/* Quick Date buttons */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500 font-medium">Accesos rápidos:</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setStartDate(todayStr);
                    setEndDate(todayStr);
                  }}
                  className="h-7 text-xs border-slate-200 hover:bg-slate-50"
                >
                  Hoy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setStartDate(lastWeekStr);
                    setEndDate(todayStr);
                  }}
                  className="h-7 text-xs border-slate-200 hover:bg-slate-50"
                >
                  Últimos 7 días
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const d15 = new Date(Date.now() - 15 * 86400000).toISOString().split("T")[0];
                    setStartDate(d15);
                    setEndDate(todayStr);
                  }}
                  className="h-7 text-xs border-slate-200 hover:bg-slate-50"
                >
                  Últimos 15 días (Quincena)
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const d30 = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
                    setStartDate(d30);
                    setEndDate(todayStr);
                  }}
                  className="h-7 text-xs border-slate-200 hover:bg-slate-50"
                >
                  Últimos 30 días (Mes)
                </Button>
              </div>

              {/* Progreso en Vivo de Extracción por Lotes */}
              {generationJob && (
                <div
                  className={`p-4 rounded-xl border transition-all ${
                    generationJob.status === "completed"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : generationJob.status === "error"
                      ? "bg-rose-50 border-rose-200 text-rose-900"
                      : "bg-sky-50 border-sky-200 text-sky-900"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {generationJob.status === "processing" ? (
                      <Loader2 className="w-5 h-5 text-sky-600 animate-spin shrink-0" />
                    ) : generationJob.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
                    )}
                    <div className="flex-1 space-y-0.5">
                      <div className="font-semibold text-sm">
                        {generationJob.status === "processing" && "Descargando marcajes por lotes..."}
                        {generationJob.status === "completed" && "¡Descarga de marcajes completada!"}
                        {generationJob.status === "error" && "Error en la descarga de marcajes"}
                      </div>
                      <div className="text-xs opacity-90">{generationJob.progress}</div>
                      {generationJob.status === "processing" && (
                        <div className="text-[11px] text-slate-500 pt-1">
                          Nota técnica: La API de CrossChex Cloud entrega los datos por lotes y requiere 31s entre cada petición para recopilar el 100% de los registros sin omitir marcas.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  onClick={handleGenerateExcel}
                  disabled={generating}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm flex items-center justify-center gap-2"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Descargando por lotes...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Generar y Descargar Excel (.xlsx)
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

        </TabsContent>

        {/* Tab 2: Configuración de Empresas & APIs (Solo Administrador) */}
        {isAdmin && (
          <TabsContent value="configuracion" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">
                Parametriza nuevas empresas para vincular sus credenciales de CrossChex Cloud u otros biométricos.
              </p>
              <Button
                onClick={handleOpenNewCompany}
                className="bg-orange-600 hover:bg-orange-700 text-white shadow-sm flex items-center gap-2"
              >
                <PlusCircle className="w-4 h-4" />
                Nueva Empresa
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {empresas.map((comp) => (
                <Card key={comp.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
                    <div className="space-y-1">
                      <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-sky-600" />
                        {comp.nombre}
                      </CardTitle>
                      <CardDescription className="text-xs text-slate-500">
                        Proveedor: <strong>{comp.proveedor}</strong> | UTC Offset: {comp.timezone_offset}h
                      </CardDescription>
                    </div>
                    <Badge className={comp.activo ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                      {comp.activo ? "Activo" : "Inactivo"}
                    </Badge>
                  </CardHeader>
                  <CardContent className="space-y-3 text-xs">
                    <div className="bg-slate-50 p-2.5 rounded-lg space-y-1 text-slate-600 border border-slate-100">
                      <div>
                        <span className="font-semibold text-slate-700">URL de API:</span> {comp.api_url}
                      </div>
                      <div>
                        <span className="font-semibold text-slate-700">API Key:</span> <code>{comp.api_key}</code>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestConnection(comp.id)}
                        disabled={testingConnection}
                        className="flex-1 border-slate-200 hover:bg-slate-100 text-slate-700 text-xs flex items-center justify-center gap-1.5"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        Probar Conexión
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCompany(comp.id)}
                        className="text-rose-600 hover:bg-rose-50 h-8 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}

      </Tabs>

      {/* Modal: Nueva / Editar Empresa */}
      <Dialog open={isCompanyModalOpen} onOpenChange={setIsCompanyModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Building2 className="w-5 h-5 text-sky-600" />
              {editingCompanyId ? "Editar Empresa de Marcaje" : "Registrar Nueva Empresa"}
            </DialogTitle>
            <DialogDescription>
              Ingresa las llaves de acceso provistas por CrossChex Cloud para habilitar la sincronización.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCompany} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="comp_name">Nombre de la Empresa / Sede *</Label>
              <Input
                id="comp_name"
                placeholder="Ej. Miami, Guatemala Central, Bodega..."
                value={companyForm.nombre}
                onChange={(e) => setCompanyForm({ ...companyForm, nombre: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="comp_url">URL de API</Label>
              <Input
                id="comp_url"
                value={companyForm.api_url}
                onChange={(e) => setCompanyForm({ ...companyForm, api_url: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="comp_key">API Key *</Label>
              <Input
                id="comp_key"
                placeholder="fbfcb9a1c24d376494c3e2481c15094e"
                value={companyForm.api_key}
                onChange={(e) => setCompanyForm({ ...companyForm, api_key: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="comp_sec">API Secret *</Label>
              <Input
                id="comp_sec"
                type="password"
                placeholder="5f46467f2e65a612053db88d36960d32"
                value={companyForm.api_secret}
                onChange={(e) => setCompanyForm({ ...companyForm, api_secret: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="comp_tz">Diferencia Horaria (Horas respecto a UTC)</Label>
              <Input
                id="comp_tz"
                type="number"
                placeholder="-5"
                value={companyForm.timezone_offset}
                onChange={(e) => setCompanyForm({ ...companyForm, timezone_offset: parseInt(e.target.value) || 0 })}
                required
              />
              <p className="text-[11px] text-slate-400">Ejemplo: -5 para Miami (EST) o -6 para Guatemala (CST)</p>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsCompanyModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={savingCompany} className="bg-sky-600 hover:bg-sky-700 text-white">
                {savingCompany ? "Guardando..." : "Guardar Empresa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
